import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon, CheckIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const FR: Record<string, string> = {
  Family: 'Famille',
  Properties: 'Propriétés',
  Vehicles: 'Véhicules',
  Memberships: 'Adhésions',
  Licenses: 'Permis',
  Subscriptions: 'Abonnements',
  Loyalty: 'Fidélité',
  'Access Rights': "Droits d'accès",
  Person: 'Personne',
  Property: 'Propriété',
  Vehicle: 'Véhicule',
  Animal: 'Animal',
  License: 'Permis',
  Membership: 'Adhésion',
  Subscription: 'Abonnement',
  Benefits: 'Avantages',
  'Family Plans': 'Forfaits familiaux',
  'Shared Memberships': 'Adhésions partagées',
  'Resident Pricing': 'Tarif résident',
  'Parking Access': 'Accès au stationnement',
  'Licenses & Permits': 'Permis et licences',
  'Loyalty & Rewards': 'Fidélité et récompenses',
  Assets: 'Actifs',
  Citizen: 'Citoyen',
  Student: 'Étudiant',
  Traveler: 'Voyageur',
  Customer: 'Client',
  Household: 'Foyer',
  Dependents: 'Personnes à charge',
  Companions: 'Accompagnateurs',
  'Family Account': 'Compte familial',
  'Property / Vehicle': 'Propriété / Véhicule',
  Devices: 'Appareils',
  'Luggage / Vehicle': 'Bagages / Véhicule',
  'Registered Products': 'Produits enregistrés',
  'Resident Status': 'Statut de résident',
  Enrollment: 'Inscription',
  'Frequent Flyer': 'Grand voyageur',
  'Loyalty Tier': 'Niveau de fidélité',
  Subsidies: 'Subventions',
  Grants: 'Bourses',
  'Lounge Access': 'Accès au salon',
  Rewards: 'Récompenses',
  Facilities: 'Installations',
  'Campus Buildings': 'Bâtiments du campus',
  'Fast Track': 'Voie rapide',
  'Exclusive Events': 'Événements exclusifs',
  'Download App': "Télécharger l'appli",
  'Activate Card': 'Activer la carte',
  'Verify Identity': "Vérifier l'identité",
  'Connect Relationships': 'Connecter les relations',
  'Access Services': 'Accéder aux services',
  'Receive Benefits': 'Recevoir les avantages',
  'Use ATNA Experiences': 'Utiliser les expériences ATNA',
  'NFC Access': 'Accès NFC',
  'Membership Validation': "Validation d'adhésion",
  'Resident Verification': 'Vérification de résidence',
  'Discount Programs': 'Programmes de rabais',
  'Loyalty Programs': 'Programmes de fidélité',
  'Family Accounts': 'Comptes familiaux'
};
const DEMO_FR: Record<string, string> = {
  Household: 'Foyer',
  '2 members': '2 membres',
  Vehicle: 'Véhicule',
  'Registration active': 'Immatriculation active'
};
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const VALUE_CARDS = [{
  title: 'Family Plans',
  subtitle: 'Service + Transaction'
}, {
  title: 'Shared Memberships',
  subtitle: 'Service + Transaction'
}, {
  title: 'Resident Pricing',
  subtitle: 'Service + Transaction'
}, {
  title: 'Parking Access',
  subtitle: 'Service + Transaction'
}, {
  title: 'Licenses & Permits',
  subtitle: 'Service + Transaction'
}, {
  title: 'Loyalty & Rewards',
  subtitle: 'Service + Transaction'
}];
const COMPARISON_ROWS = [{
  label: 'Person',
  cities: 'Citizen',
  associations: 'Member',
  universities: 'Student',
  airports: 'Traveler',
  retail: 'Customer'
}, {
  label: 'Family',
  cities: 'Household',
  associations: 'Family Plan',
  universities: 'Dependents',
  airports: 'Companions',
  retail: 'Family Account'
}, {
  label: 'Assets',
  cities: 'Property / Vehicle',
  associations: 'Equipment',
  universities: 'Devices',
  airports: 'Luggage / Vehicle',
  retail: 'Registered Products'
}, {
  label: 'Membership',
  cities: 'Resident Status',
  associations: 'Tier Level',
  universities: 'Enrollment',
  airports: 'Frequent Flyer',
  retail: 'Loyalty Tier'
}, {
  label: 'Benefits',
  cities: 'Subsidies',
  associations: 'Discounts',
  universities: 'Grants',
  airports: 'Lounge Access',
  retail: 'Rewards'
}, {
  label: 'Access Rights',
  cities: 'Facilities',
  associations: 'Events',
  universities: 'Campus Buildings',
  airports: 'Fast Track',
  retail: 'Exclusive Events'
}];
export function DigitalCard() {
  const {
    lang
  } = useLanguage();
  const t = (en: string) => lang === 'FR' ? FR[en] ?? en : en;
  const demoT = (en: string) => lang === 'FR' ? DEMO_FR[en] ?? en : en;
  useEffect(() => {
    track('Page Viewed', {
      page: 'Digital Card'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-2099">
      <Header data-id="element-2100" />

      <main className="pt-[120px]" data-id="element-2101">
        {/* HERO SECTION */}
        <section className="pt-24 pb-32 px-6" data-id="element-2102">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2103">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-2104">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-indigo uppercase mb-6" data-id="element-2105">
                {lang === 'FR' ? 'Carte BCITI' : 'BCITI Card'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-2106">
                {lang === 'FR' ? 'Une carte. Chaque relation vérifiée.' : 'One Card. Every Relationship Verified.'}
              </h1>
              <p className="font-sans text-xl text-[#121212]/70 leading-relaxed mb-6" data-id="element-2107">
                {lang === 'FR' ? "Une identité de confiance reliée aux familles, aux adhésions, aux propriétés, aux véhicules, aux actifs, aux abonnements et aux droits d'accès." : 'A trusted identity connected to families, memberships, properties, vehicles, assets, subscriptions, and access rights.'}
              </p>
              <p className="font-sans text-lg text-[#121212]/60 leading-relaxed mb-10" data-id="element-2108">
                {lang === 'FR' ? 'Conçue une fois. Adaptable aux villes, universités, aéroports, associations, commerces et organisations à membres.' : 'Built once. Adaptable across cities, universities, airports, associations, retailers, and member organizations.'}
              </p>
              <div className="flex flex-wrap items-center gap-6" data-id="element-2109">
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Digital Card Hero'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors" data-id="element-2110">
                  {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2111" />
                </Link>
                <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-base font-medium text-[#121212] hover:text-indigo transition-colors" data-id="element-2112">
                  {lang === 'FR' ? 'Découvrir Brossard' : 'Explore Brossard'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2113" />
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative flex justify-center items-center h-[500px]" data-id="element-2114">
              <img src={lang === 'FR' ? 'https://cdn.magicpatterns.com/uploads/uHyx7j3K2c4rYJ9FYAKX8r/ChatGPT_Image_7_juil._2026,_13_42_38.png' : 'https://cdn.magicpatterns.com/uploads/oyN36MaM75956RY7Hf1rfF/ChatGPT_Image_7_juil._2026,_13_38_42.png'} alt={lang === 'FR' ? "Application Carte BCITI reliée à la famille, aux propriétés, aux véhicules, aux animaux, aux permis, aux abonnements, aux adhésions et aux droits d'accès" : 'BCITI Card app connected to family, properties, vehicles, pets, licenses, subscriptions, memberships and access rights'} className="w-full h-auto object-contain max-w-md mx-auto" data-id="element-2115" />
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: NOT A CARD. A VERIFIED RELATIONSHIP GRAPH. */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2116">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2117">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-12" data-id="element-2118">
              {lang === 'FR' ? 'Les cartes identifient. La Carte BCITI comprend.' : 'Cards identify. BCITI Card understands.'}
            </motion.h2>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="space-y-6 font-sans text-xl text-[#121212]/70 leading-relaxed mb-20 max-w-3xl mx-auto" data-id="element-2119">
              <p data-id="element-2120">
                {lang === 'FR' ? 'Les cartes traditionnelles identifient les personnes.' : 'Traditional cards identify people.'}
              </p>
              <p className="text-indigo font-medium" data-id="element-2121">
                {lang === 'FR' ? 'La Carte BCITI comprend les relations.' : 'BCITI Card understands relationships.'}
              </p>
              <p data-id="element-2122">
                {lang === 'FR' ? "Une personne est reliée à un foyer, à des adhésions, des véhicules, des permis, des abonnements, des avantages et des droits d'accès." : 'A person is connected to a household, memberships, vehicles, licenses, subscriptions, benefits, and access rights.'}
              </p>
              <p data-id="element-2123">
                {lang === 'FR' ? 'Ce réseau de relations alimente les services, les transactions et l’intelligence.' : 'That relationship network powers services, transactions, and intelligence.'}
              </p>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-wrap justify-center items-center gap-4" data-id="element-2124">
              <div className="px-6 py-3 border border-indigo bg-indigo/5 text-indigo font-serif text-lg" data-id="element-2125">
                {lang === 'FR' ? 'Carte BCITI' : 'BCITI Card'}
              </div>
              <ArrowRightIcon className="w-5 h-5 text-[#E4E6EC]" data-id="element-2126" />
              <div className="flex flex-wrap justify-center gap-2" data-id="element-2127">
                {['Person', 'Family', 'Property', 'Vehicle', 'Animal', 'License', 'Membership', 'Subscription', 'Benefits'].map(item => <div key={item} className="px-4 py-2 border border-[#E4E6EC] bg-white text-[#121212]/60 font-sans text-sm" data-id="element-2128">
                    {t(item)}
                  </div>)}
              </div>
            </motion.div>

            {/* Phone mockup — BCITI Card inside the app (illustrative) */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="flex flex-col items-center mt-20" data-id="element-2129">
              {lang === 'FR' ? <img src="https://cdn.magicpatterns.com/uploads/eqNnXTaJtf8zAL99M7ppDD/ChatGPT_Image_7_juil._2026,_13_50_15.png" alt="Application Carte BCITI — carte d'identité numérique citoyenne de Marie Tremblay avec ses liens connectés : famille, véhicule, propriété, adhésion et abonnement" className="w-[280px] h-auto mx-auto" data-id="element-2130" /> : <img src="https://cdn.magicpatterns.com/uploads/28jQomEgNYp1hxpV8x7trp/ChatGPT_Image_7_juil._2026,_13_54_09.png" alt="BCITI Card app — Marie Tremblay's digital citizen identity with her connected relationships: household, vehicle, property, membership and subscription" className="w-[280px] h-auto mx-auto" data-id="element-2131" />}
              <p className="font-sans text-[11px] text-[#121212]/40 mt-4" data-id="element-2132">
                {lang === 'FR' ? 'Vue produit illustrative' : 'Illustrative product view'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: EVERY RELATIONSHIP CREATES VALUE */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2133">
          <div className="max-w-6xl mx-auto" data-id="element-2134">
            <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-2135">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6 leading-tight" data-id="element-2136">
                {lang === 'FR' ? <>
                    Chaque relation devient un service.
                    <br data-id="element-2137" />
                    Chaque service devient une transaction.
                  </> : <>
                    Every relationship becomes a service.
                    <br data-id="element-2138" />
                    Every service becomes a transaction.
                  </>}
              </motion.h2>
              <motion.p {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="font-sans text-xl text-[#121212]/60" data-id="element-2139">
                {lang === 'FR' ? 'Plus la plateforme comprend de relations, plus elle crée de valeur.' : 'The more relationships the platform understands, the more value it creates.'}
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16" data-id="element-2140">
              {VALUE_CARDS.map((card, i) => <motion.div key={card.title} {...reveal} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="border border-[#E4E6EC] p-8 hover:border-indigo/30 transition-colors flex flex-col justify-between h-40" data-id="element-2141">
                  <h3 className="font-serif text-2xl text-[#121212]" data-id="element-2142">
                    {t(card.title)}
                  </h3>
                  <div className="font-sans text-sm font-medium tracking-widest text-indigo uppercase" data-id="element-2143">
                    {card.subtitle}
                  </div>
                </motion.div>)}
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center" data-id="element-2144">
              <p className="font-sans text-sm text-[#121212]/50 tracking-wide" data-id="element-2145">
                {lang === 'FR' ? "Chaque relation vérifiée crée de nouvelles occasions d'engagement et de nouvelles sources de revenus." : 'Each verified relationship creates new engagement opportunities and new revenue streams.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 4: ONE PLATFORM. MULTIPLE INDUSTRIES. */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2146">
          <div className="max-w-6xl mx-auto" data-id="element-2147">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-6" data-id="element-2148">
              {lang === 'FR' ? "L'architecture reste la même." : 'The architecture stays the same.'}
            </motion.h2>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/60 text-center mb-20" data-id="element-2149">
              {lang === 'FR' ? 'Seules les étiquettes changent.' : 'Only the labels change.'}
            </motion.p>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="overflow-x-auto pb-8" data-id="element-2150">
              <table className="w-full min-w-[800px] border-collapse bg-white border border-[#E4E6EC]" data-id="element-2151">
                <thead data-id="element-2152">
                  <tr className="border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2153">
                    <th className="py-6 px-6 text-left font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase w-1/5" data-id="element-2154">
                      {lang === 'FR' ? 'Couche de relation' : 'Relationship Layer'}
                    </th>
                    <th className="py-6 px-6 text-left font-sans text-xs font-semibold tracking-widest text-[#121212]/80 uppercase w-1/5" data-id="element-2155">
                      {lang === 'FR' ? 'Villes' : 'Cities'}
                    </th>
                    <th className="py-6 px-6 text-left font-sans text-xs font-semibold tracking-widest text-[#121212]/80 uppercase w-1/5" data-id="element-2156">
                      {lang === 'FR' ? 'Universités' : 'Universities'}
                    </th>
                    <th className="py-6 px-6 text-left font-sans text-xs font-semibold tracking-widest text-[#121212]/80 uppercase w-1/5" data-id="element-2157">
                      {lang === 'FR' ? 'Aéroports' : 'Airports'}
                    </th>
                    <th className="py-6 px-6 text-left font-sans text-xs font-semibold tracking-widest text-[#121212]/80 uppercase w-1/5" data-id="element-2158">
                      {lang === 'FR' ? 'Commerce & Télécom' : 'Retail & Telecom'}
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-2159">
                  {COMPARISON_ROWS.map((row, i) => <tr key={row.label} className="border-b border-[#E4E6EC] last:border-0 hover:bg-[#FAFAFB]/50 transition-colors" data-id="element-2160">
                      <td className="py-5 px-6 font-serif text-lg text-indigo border-r border-[#E4E6EC]" data-id="element-2161">
                        {t(row.label)}
                      </td>
                      <td className="py-5 px-6 font-sans text-sm text-[#121212]/70" data-id="element-2162">
                        {t(row.cities)}
                      </td>
                      <td className="py-5 px-6 font-sans text-sm text-[#121212]/70" data-id="element-2163">
                        {t(row.universities)}
                      </td>
                      <td className="py-5 px-6 font-sans text-sm text-[#121212]/70" data-id="element-2164">
                        {t(row.airports)}
                      </td>
                      <td className="py-5 px-6 font-sans text-sm text-[#121212]/70" data-id="element-2165">
                        {t(row.retail)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </motion.div>
          </div>
        </section>

        {/* SECTION 5: PROVEN IN PRODUCTION */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2166">
          <div className="max-w-4xl mx-auto" data-id="element-2167">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-16" data-id="element-2168">
              {lang === 'FR' ? 'Conçue et affinée sur neuf ans.' : 'Built and refined over nine years.'}
            </motion.h2>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="bg-white border border-indigo/20 p-12 relative shadow-sm" data-id="element-2169">
              <div className="absolute -top-4 left-12 bg-indigo text-white font-sans text-xs font-bold tracking-widest uppercase px-4 py-2" data-id="element-2170">
                {lang === 'FR' ? 'En production' : 'Live in Production'}
              </div>

              <div className="mb-12" data-id="element-2171">
                <h3 className="font-serif text-3xl text-[#121212] mb-2" data-id="element-2172">
                  {lang === 'FR' ? 'Ville de Brossard' : 'City of Brossard'}
                </h3>
                <p className="font-sans text-[#121212]/60" data-id="element-2173">
                  {lang === 'FR' ? 'Au service quotidien de vraies communautés, pas de projets pilotes.' : 'Trusted daily by real communities, not pilot projects.'}
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12" data-id="element-2174">
                <div data-id="element-2175">
                  <div className="font-serif text-3xl text-[#121212] mb-2" data-id="element-2176">
                    88,266
                  </div>
                  <div className="font-sans text-sm text-[#121212]/60" data-id="element-2177">
                    {lang === 'FR' ? 'Identités vérifiées' : 'Verified Identities'}
                  </div>
                </div>
                <div data-id="element-2178">
                  <div className="font-serif text-3xl text-[#121212] mb-2" data-id="element-2179">
                    78%
                  </div>
                  <div className="font-sans text-sm text-[#121212]/60" data-id="element-2180">
                    {lang === 'FR' ? 'Couverture de la population' : 'Population Coverage'}
                  </div>
                </div>
                <div data-id="element-2181">
                  <div className="font-serif text-3xl text-[#121212] mb-2" data-id="element-2182">
                    Since 2017
                  </div>
                  <div className="font-sans text-sm text-[#121212]/60" data-id="element-2183">
                    {lang === 'FR' ? '9 ans en production' : '9 Years in Production'}
                  </div>
                </div>
                <div data-id="element-2184">
                  <div className="font-serif text-3xl text-[#121212] mb-2" data-id="element-2185">
                    20+
                  </div>
                  <div className="font-sans text-sm text-[#121212]/60" data-id="element-2186">
                    {lang === 'FR' ? 'Communautés' : 'Communities'}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t border-[#E4E6EC]" data-id="element-2187">
                <div className="flex items-center gap-6 font-sans text-sm text-[#121212]/50" data-id="element-2188">
                  <span data-id="element-2189">
                    {lang === 'FR' ? 'Conforme Loi 25' : 'Law 25 Ready'}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-[#E4E6EC]" data-id="element-2190" />
                  <span data-id="element-2191">
                    {lang === 'FR' ? 'Infrastructure hébergée au Canada' : 'Canadian Hosted Infrastructure'}
                  </span>
                </div>
                <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-sm font-medium text-indigo hover:text-indigo/80 transition-colors" data-id="element-2192">
                  {lang === 'FR' ? "Découvrir l'étude de cas Brossard" : 'Explore the Brossard Case Study'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2193" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 6: HOW IT WORKS */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2194">
          <div className="max-w-6xl mx-auto" data-id="element-2195">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-20" data-id="element-2196">
              {lang === 'FR' ? 'Simple pour les usagers. Puissant pour les organisations.' : 'Simple for users. Powerful for organizations.'}
            </motion.h2>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="mb-24" data-id="element-2197">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-2198">
                {['Download App', 'Activate Card', 'Verify Identity', 'Connect Relationships', 'Access Services', 'Receive Benefits', 'Use ATNA Experiences'].map((step, i, arr) => <Fragment key={step} data-id="element-2199">
                    <div className={`text-center p-4 border ${i === 3 ? 'border-indigo bg-indigo/5' : 'border-[#E4E6EC] bg-white'} w-full md:w-auto flex-1`} data-id="element-2200">
                      <span className={`font-sans text-sm ${i === 3 ? 'text-indigo font-medium' : 'text-[#121212]/70'}`} data-id="element-2201">
                        {t(step)}
                      </span>
                    </div>
                    {i < arr.length - 1 && <div className="text-[#E4E6EC] rotate-90 md:rotate-0" data-id="element-2202">
                        <ArrowRightIcon className="w-5 h-5" data-id="element-2203" />
                      </div>}
                  </Fragment>)}
              </div>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-2204">
              <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase text-center mb-8" data-id="element-2205">
                {lang === 'FR' ? 'Capacités optionnelles' : 'Optional Capabilities'}
              </div>
              <div className="flex flex-wrap justify-center gap-4" data-id="element-2206">
                {['NFC Access', 'Membership Validation', 'Resident Verification', 'Subscriptions', 'Discount Programs', 'Loyalty Programs', 'Family Accounts'].map(cap => <div key={cap} className="px-5 py-2.5 border border-[#E4E6EC] bg-white font-sans text-sm text-[#121212]/60" data-id="element-2207">
                    {t(cap)}
                  </div>)}
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 7: THE FUTURE OF TRUSTED COMMUNITIES */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2208">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2209">
            <motion.div {...reveal} className="font-sans text-sm font-semibold tracking-widest text-indigo uppercase mb-8" data-id="element-2210">
              {lang === 'FR' ? 'Citoyens. Membres. Étudiants. Voyageurs. Clients.' : 'Citizens. Members. Students. Travelers. Customers.'}
            </motion.div>
            <motion.h2 {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-serif text-4xl md:text-5xl text-[#121212] mb-10" data-id="element-2211">
              {lang === 'FR' ? "Une carte s'adapte à tous." : 'One card adapts to all.'}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="space-y-6 font-sans text-xl text-[#121212]/70 leading-relaxed" data-id="element-2212">
              <p data-id="element-2213">
                {lang === 'FR' ? 'Les communautés les plus fortes reposent sur des relations de confiance.' : 'The strongest communities are built on trusted relationships.'}
              </p>
              <p data-id="element-2214">
                {lang === 'FR' ? 'La Carte BCITI crée la fondation qui relie les personnes, les services, les transactions et l’intelligence.' : 'BCITI Card creates the foundation that connects people, services, transactions, and intelligence.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-40 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2215">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2216">
            <motion.h2 {...reveal} className="font-serif text-5xl text-[#121212] mb-8" data-id="element-2217">
              {lang === 'FR' ? 'Bâtissez votre réseau de relations de confiance.' : 'Build your trusted relationship network.'}
            </motion.h2>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/60 mb-12" data-id="element-2218">
              {lang === 'FR' ? 'Une identité. Une carte. Des possibilités infinies.' : 'One identity. One card. Infinite possibilities.'}
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-2219">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Digital Card Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors" data-id="element-2220">
                {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2221" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-2222" />
    </div>;
}